import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Dialog, DialogContent, Divider, Typography } from "@mui/material";
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockResetIcon from '@mui/icons-material/LockReset';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { AppColor, Fonts } from "../../../types/Constants";
import { useAuthContext } from "../contexts/AuthContext";
import AppConfirmDialog from "../../../components/AppConfirmDialog";
import ChangePasswordDialog from "./ChangePasswordDialog";

interface UserProfileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isSSO: boolean;
}

const UserProfileDialog: React.FC<UserProfileDialogProps> = (prop) => {

  const auth = useAuthContext();
  const navigate = useNavigate();

  const [openLogoutConfirmation, setOpenLogoutConfirmation] = useState<boolean>(false);
  const [openChangePassword, setOpenChangePassword] = useState<boolean>(false);

  const subscriptionButton = () => {
    navigate("/subscriptions");
    prop.onClose();
  };

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: "white",
            position: "absolute",
            top: 28,
            right: 0,
            marginRight: 1
          },
          '& .MuiDialogContent-root': {
            overflowY: 'hidden',
            paddingLeft: 0,
            paddingRight: 0,
            paddingBottom: 0
          }
        }}
        open={prop.isOpen}
        onClose={() => prop.onClose()}
        hideBackdrop={true}
      >
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", mx: 2, mt: 0 }}>
            <Box sx={{
              display: "flex",
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: AppColor.baseOrange,
              color: "white"
            }}>
              <PersonOutlineOutlinedIcon sx={{ color: "white", fontSize: 24, fontWeight: Fonts.LIGHT }} />
            </Box>
            <Box sx={{ ml: 2, mt: 2 }}>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: Fonts.SEMI_BOLD,
                  wordBreak: "break-all",
                  color: AppColor.headerText,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '2',
                  WebkitBoxOrient: 'vertical'
                }}>
                {auth.preferredUserName ?? auth.user}
              </Typography>
            </Box>
            <Box sx={{ maxWidth: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", mt: 5 }}>
                <EmailOutlinedIcon sx={{ color: AppColor.subHeaderText, fontSize: 20 }} />
                <Typography sx={{ ml: 1, fontSize: 14 }}>
                  {auth.user}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ mt: 6 }}>
            <Divider sx={{ borderColor: AppColor.orangeBoldBackground }} />
            <Button sx={{ width: "100%", justifyContent: "flex-start" }}
              onClick={subscriptionButton}>
              <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", my: 1, color: AppColor.headerText }}>
                <CreditCardIcon sx={{ fontSize: 22 }} />
                <Typography sx={{ ml: 1, fontSize: 15 }}>
                  Subscription
                </Typography>
              </Box>
            </Button>
            {!prop.isSSO && (
              <>
                <Divider sx={{ borderColor: AppColor.orangeBackground }} />
                <Button sx={{ width: "100%", justifyContent: "flex-start" }}
                  onClick={() => setOpenChangePassword(true)}>
                  <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", my: 1, color: AppColor.headerText }}>
                    <LockResetIcon sx={{ fontSize: 22 }} />
                    <Typography sx={{ ml: 1, fontSize: 15 }}>
                      Change Password
                    </Typography>
                  </Box>
                </Button>
              </>
            )}
            <Divider sx={{ borderColor: AppColor.orangeBackground }} />
            <Button sx={{ width: "100%", justifyContent: "flex-start" }}
              onClick={() => setOpenLogoutConfirmation(true)}>
              <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", my: 1, color: AppColor.headerText }}>
                <ExitToAppOutlinedIcon sx={{ fontSize: 22 }} />
                <Typography sx={{ ml: 1, fontSize: 15 }}>Log Out</Typography>
              </Box>
            </Button>
            <Divider sx={{ borderColor: AppColor.orangeBackground }} />
          </Box>
        </DialogContent>
      </Dialog>
      <ChangePasswordDialog
        isOpen={openChangePassword}
        onClose={() => {
          prop.onClose();
          setOpenChangePassword(false);
        }}
      />
      <AppConfirmDialog
        open={openLogoutConfirmation}
        onConfirm={() => {
          setOpenLogoutConfirmation(false);
          prop.onClose();
          auth.signout(() => navigate("/"));
        }}
        onDeny={() => {
          setOpenLogoutConfirmation(false);
        }}
        title="You are about to log out. Do you want to proceed?"
        dialogTitle="Logout"
        content=""
      />
    </>
  );
};

export default UserProfileDialog;
